import { MIME_TYPE_BY_EXTENSION } from '@/constants'
import { IconNameType, DataType, SelectItemType, RecordsForUpdateType, ProjectFileType } from '@/types'
import { useToast } from '@/ui'

export const keyToLabel = (key: string): string =>
  key.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

const truncateStr = (str: string = '', maxLen: number = 20) =>
  str.length > maxLen ? `${str.substring(0, maxLen)}...` : str

export const isDataEmpty = (data: DataType | DataType[], withoutObjects?: boolean): boolean => {
  if (data === undefined || data === null) {
    return true
  }

  if (typeof data === 'string') {
    return data.trim() === ''
  }

  if (Array.isArray(data)) {
    return data.filter(item => !isDataEmpty(item, true)).length === 0
  }

  if (typeof data === 'object') {
    return withoutObjects || Object.keys(data).length === 0 || !Object.values(data).some(item => !isDataEmpty(item))
  }

  return false
}

export const valueToBoolean = (value?: DataType): boolean => {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value !== 0
  }

  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim()
    return (
      lowerValue !== '' &&
      lowerValue !== '0' &&
      lowerValue !== 'false' &&
      lowerValue !== 'null' &&
      lowerValue !== 'undefined'
    )
  }

  return false
}

export const dataToString = (data: string[] | string | number, delimiter = ', ') => {
  if (Array.isArray(data)) {
    return data.join(delimiter)
  }

  return typeof data === 'string' ? truncateStr(data) : String(data)
}

export const dataToFormData = (data: Record<string, DataType>): FormData => {
  const formData = new FormData()

  const appendFormData = (formData: FormData, key: string, value: DataType) => {
    if (value === 'undefined' || value === undefined || value === null) {
      return
    }

    if (typeof value === 'object' && !(value instanceof File)) {
      Object.entries(value as Record<string, object>).forEach(([nestedKey, nestedValue]) => {
        if (nestedValue === undefined || nestedValue === null) {
          return
        }
        appendFormData(formData, `${key}[${nestedKey}]`, nestedValue)
      })
    } else if (typeof value === 'boolean') {
      formData.append(key, value ? '1' : '0')
    } else {
      formData.append(key, value as string | Blob)
    }
  }

  Object.entries(data).forEach(([key, value]) => {
    appendFormData(formData, key, value)
  })

  return formData
}

export const getInitials = (obj = {}) => {
  let { name, first_name = '', last_name = '' } = obj as Record<string, string>

  if (!first_name && !last_name && name) {
    const arr = name.split(' ')
    first_name = arr[0] || ''
    last_name = arr[1] || ''
  }

  return `${first_name.charAt(0)}${last_name.charAt(0)}`
}

export const getName = (obj = {}, withDefault = true) => {
  const { name, first_name, middle_name, last_name } = (obj as Record<string, string>) || {}

  let result = [first_name, middle_name, last_name].filter(part => part && part.trim() !== '').join(' ') || name || ''

  if (!result && withDefault) {
    result = 'unknown'
  }

  return result
}

export const getFileName = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    const encodedName = parsedUrl.pathname.split('/').pop() || ''
    return decodeURIComponent(encodedName)
  } catch (error) {
    const { toast } = useToast()
    toast({
      title: 'Invalid URL or failed to decode filename:',
      description: error instanceof Error ? error.message : undefined,
      variant: 'destructive',
    })
    const arr = url.split('/')
    return arr[arr.length - 1]
  }
}

export const getFileIcon = (fileName: string): IconNameType => {
  const fileExtension = fileName.split('.').pop()
  switch (fileExtension) {
    case 'pdf':
    case 'doc':
    case 'docx':
      return 'DocumentTextIcon'
    case 'xls':
    case 'xlsx':
    case 'ppt':
    case 'pptx':
      return 'DocumentChartBarIcon'
    case 'jpeg':
    case 'png':
    case 'jpg':
    case 'svg':
      return 'PhotoIcon'
    case 'gif':
      return 'GifIcon'
    case 'mp3':
    case 'ogg':
    case 'wav':
      return 'MusicalNoteIcon'
    default:
      return 'DocumentIcon'
  }
}

export function getCategorizedFiles(files: ProjectFileType[]) {
  const categorizedFiles: Record<string, ProjectFileType[]> = {
    images: [],
    videos: [],
    audios: [],
    documents: [],
    others: [],
  }

  files.forEach(file => {
    const fileExtension = file.file_name.split('.').pop()?.toLowerCase() || ''

    if (['jpeg', 'png', 'jpg', 'svg', 'gif'].includes(fileExtension)) {
      categorizedFiles.images.push(file)
    } else if (['mp4', 'avi', 'mov', 'wmv'].includes(fileExtension)) {
      categorizedFiles.videos.push(file)
    } else if (['mp3', 'ogg', 'wav'].includes(fileExtension)) {
      categorizedFiles.audios.push(file)
    } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(fileExtension)) {
      categorizedFiles.documents.push(file)
    } else {
      categorizedFiles.others.push(file)
    }
  })

  return categorizedFiles
}

export function getMimeType(fileName: string): string {
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || ''

  return MIME_TYPE_BY_EXTENSION[fileExtension] || 'application/octet-stream'
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export const formatedField = (field: string): string => {
  return field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

export function filteringSearchValues(
  options: (string | SelectItemType)[],
  search: string = '',
  maxLength?: number,
  selected: string[] = []
) {
  const searchLower = search.toLowerCase()
  const result = [] as (string | SelectItemType)[]

  for (const item of options) {
    if (selected.length) {
      const candidateValue = typeof item === 'string' ? item : String(item.id ?? '')
      if (selected.includes(candidateValue)) {
        continue
      }
    }

    const candidateName = typeof item === 'string' ? item : (item.name ?? '')

    if (candidateName.toLowerCase().includes(searchLower)) {
      result.push(item)

      if (maxLength && result.length >= maxLength) {
        break
      }
    }
  }

  return result as string[] | Record<string, string>[]
}

export function getFormattedPrice(
  val?: string | number,
  opts: {
    currency?: string
    noCents?: boolean
    locale?: string | string[]
  } = {}
): string {
  if (val == null || val === '') return ''

  const { currency = 'USD', noCents = false, locale = 'en-US' } = opts

  const num = typeof val === 'string' ? Number(val) : val
  if (!Number.isFinite(num as number)) return ''

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: noCents ? 0 : undefined,
    maximumFractionDigits: noCents ? 0 : undefined,
  })

  return formatter.format(num as number)
}

export function editRecordsForUpdate<T>(
  records: RecordsForUpdateType<T>,
  id: string,
  field: string,
  value: string | undefined
) {
  if (value === undefined) {
    delete records[id]?.[field]

    if (Object.keys(records[id] || {}).length === 0) {
      delete records[id]
    }
  } else {
    if (!records[id]) {
      records[id] = {} as T
    }

    records[id] = {
      ...records[id],
      [field]: value,
    }
  }

  return records
}
